import React, { useState, useContext, useEffect } from 'react'
import { Table, Button, Input, Space, Tag, Menu, Dropdown, Tooltip } from 'antd'
import {
  SearchOutlined,
  SendOutlined,
  LinkOutlined,
  DownOutlined,
} from '@ant-design/icons'

import moment from 'moment'
import { AppContext } from 'AppContext'
import Page404 from 'components/Page404'
import {
  GET_NEW_TICKETS,
  GET_TICKET,
  GET_NEW_TICKET,
  GET_PRIORITY_SLA_CONFIG,
} from '../graphql/Queries'
import { GET_USERS } from 'modules/Users/graphql/Queries'
import client from 'apollo'
import CloseTicketModal from '../CloseTicketModal'
import { REPLY_TICKET_MODULE, NEW_UPDATE_TICKET } from '../graphql/Mutations'
import openNotification from 'utils/Notification'
import { uniqBy, isEmpty } from 'lodash'
import ReplyTicketModal from '../ReplyTicketModal'
import { GET_TICKET_USERS } from '../Settings/graphql/Queries'
import JSZip from 'jszip'
import Attachment from 'assets/images/new-images/attachment.png'
import NoAttachment from 'assets/images/new-images/no-attachment.png'
import { getName, canUserDownload } from 'utils/User'
import DocumentViewerModal from 'components/DocumentViewerModal'


const priorityColor = {
  high: 'red',
  medium: 'orange',
  low: 'green',
}

const createStatusStyle = (r, g, b) => ({
  bg: `rgba(${r}, ${g}, ${b}, 0.15)`,
  color: `rgb(${r}, ${g}, ${b})`,
})

const statusPalette = {
  ALL: createStatusStyle(0, 32, 96),
  NEW: createStatusStyle(251, 188, 4),
  OPEN: createStatusStyle(0, 176, 80),
  INPROGRESS: createStatusStyle(252, 54, 181),
  ASSIGNED: createStatusStyle(62, 151, 255),
  BREACHED: createStatusStyle(251, 123, 4),
  ESCALATED: createStatusStyle(175, 0, 0),
  RESOLVED: createStatusStyle(19, 128, 108),
  CLOSED: createStatusStyle(60, 125, 34),
  REOPEN: createStatusStyle(255, 0, 0),
  REVIEW: createStatusStyle(126, 130, 153),
}

export default function TabTicketsNew({
  defaultFilters = {},
  setSort,
  skip,
  setSkip,
  setLimit,
  setFilters,
  limit,
  filters,
  loading,
  error,
  currentPage,
  setCurrentPage,
  data,
  totalCount = 0,
  rowSelection,
  departmentData = [],
}) {
  const [showFormModal, setShowFormModal] = useState(false)

  const [openReplyModal, setOpenReplyModal] = useState(false)
  const [showCloseButton, setShowCloseButton] = useState(false)
  const [selectedTicket, setSelectedTicket] = useState({})
  const [replyLoading, setReplyLoading] = useState(false)
  const [closeLoading, setCloseLoading] = useState(false)
  const [userData, setUserData] = useState([])
  const [userError, setUserError] = useState()
  const [userSearch, setUserSearch] = useState('')
  const [departmentSearch, setDepartmentSearch] = useState('')
  const [dropdownVisible, setDropdownVisible] = useState(false)
  const [assigningDepartmentIds, setAssigningDepartmentIds] = useState([]) // eslint-disable-line
  const [assigningAgentIds, setAssigningAgentIds] = useState([]) // eslint-disable-line
  // eslint-disable-next-line
  const [showActionButton, setShowActionButton] = useState(true)
  const [isSeeMore, setIsSeeMore] = useState({});

  const filteredUsers = React.useMemo(() => {
    const term = userSearch.trim().toLowerCase()
    if (!term) return userData

    return userData.filter((user) => {
      const firstName = (user.firstName || '').toLowerCase()
      const lastName = (user.lastName || '').toLowerCase()
      const role = (user.role || '').toLowerCase()

      return (
        firstName.includes(term) ||
        lastName.includes(term) ||
        role.includes(term)
      )
    })
  }, [userData, userSearch])

  const filteredDepartments = React.useMemo(() => {
    const term = departmentSearch.trim().toLowerCase()
    if (!term) return departmentData
    return departmentData.filter((dept) =>
      (dept.departmentName || '').toLowerCase().includes(term)
    )
  }, [departmentData, departmentSearch])

  useEffect(() => {
    if (!dropdownVisible) {
      setUserSearch('')
    }
  }, [dropdownVisible])

  const [isReOpen, setIsReOpen] = useState(false)
  const {
    state: {
      isClient,
      isAssociate,
      isAdmin,
      currentUser,
      isMasterAssociate,
      isSalesExecutive,
      isStaff,
      userPermissions,
    },
  } = useContext(AppContext)

  const canDownload = canUserDownload(userPermissions || currentUser, isClient)
  const [docViewer, setDocViewer] = useState({
    visible: false,
    fileUrl: '',
    files: [],
    title: '',
  })


  const [newTableData, setNewTableData] = useState([])
  const [slaConfigData, setSlaConfigData] = useState([])
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const [expandedTicketDetails, setExpandedTicketDetails] = useState({}) // Store full ticket details for expanded rows
  const [loadingTicketDetails, setLoadingTicketDetails] = useState({}) // Track loading state for each ticket
  const [sortInfo, setSortInfo] = useState({
    columnKey: null,
    order: null,
  })
  const [currentTime, setCurrentTime] = useState(moment())

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(moment())
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  const getSLAReferenceMoment = (ticket) => {
    if (ticket?.status === 'RESOLVED') {
      const resolvedMoment = moment(ticket.resolvedAt)
      if (resolvedMoment.isValid()) {
        return resolvedMoment
      }
    }

    if (ticket?.status === 'CLOSED') {
      const closedMoment = moment(ticket.closedDate)
      if (closedMoment.isValid()) {
        return closedMoment
      }
    }

    return currentTime
  }

  const calculateRealTimeSLATime = (ticket) => {
    try {
      if (!ticket?.duration) {
        return { label: '—', isNegative: false, rawValue: 0 }
      }

      const ticketDurationAt = moment(ticket.duration)

      if (!ticketDurationAt.isValid()) {
        return { label: '—', isNegative: false, rawValue: 0 }
      }

      const slaDeadline = ticketDurationAt.clone()
      const referenceMoment = getSLAReferenceMoment(ticket)
      const now = referenceMoment.clone()

      const remainingTime = moment.duration(slaDeadline.diff(now))

      if (!remainingTime.isValid() || isNaN(remainingTime.asMilliseconds())) {
        return { label: '—', isNegative: false, rawValue: 0 }
      }

      const isNegative = remainingTime.asMilliseconds() < 0
      const absoluteTime = moment.duration(
        Math.abs(remainingTime.asMilliseconds())
      )

      const days = absoluteTime.days()
      const hours = String(absoluteTime.hours()).padStart(2, '0')
      const minutes = String(absoluteTime.minutes()).padStart(2, '0')
      const seconds = String(absoluteTime.seconds()).padStart(2, '0')

      let formatted
      if (days > 0) {
        formatted = `${days}d ${hours}:${minutes}:${seconds}`
      } else {
        formatted = `${hours}:${minutes}:${seconds}`
      }

      return {
        label: isNegative ? `-${formatted}` : formatted,
        isNegative,
        rawValue: remainingTime.asMilliseconds(),
      }
    } catch (error) {
      return { label: '—', isNegative: false, rawValue: 0 }
    }
  }

  const getSLAColor = (ticket) => {
    const currentLevel = ticket?.currentLevel

    if (!currentLevel) {
      return { background: '#7E82991A', color: '#7E8299' }
    }

    switch (currentLevel) {
      case 'TEAM_LEADER':
        return { background: '#7E82991A', color: '#7E8299' }
      case 'DEPARTMENT_HEAD':
        return { background: '#FB7B041A', color: '#FB7B04' }
      case 'CRM_ADMIN':
        return { background: '#AF00001A', color: '#AF0000' }
      default:
        return { background: '#7E82991A', color: '#7E8299' }
    }
  }

  useEffect(() => {
    if (data?.newGetTickets?.tickets?.length > 0) {
      const allFormattedData =
        data?.newGetTickets?.tickets?.map((ticket) => {
          const {
            id,
            ticketNumber,
            generateFor,
            generatedBy,
            contactMode,
            description,
            status,
            priority,
            assignUser,
            isRead,
            categoryName,
            department,
            duration,
            createdAt,
            closedDate,
            fileUrl,
            currentLevel,
            closedByUser,
            ticketLogs,
          } = ticket

          return {
            id: Number(id),
            key: Number(id),
            ticketNumber: ticketNumber || '',
            generateFor: {
              firstName: generateFor?.firstName || '',
              lastName: generateFor?.lastName || '',
              clientCode:
                generateFor?.clientCode || generateFor?.userName || '',
            },
            generatedBy: {
              firstName: generatedBy?.firstName || '',
              lastName: generatedBy?.lastName || '',
              role: generatedBy?.role || '',
              clientCode:
                generatedBy?.clientCode || generatedBy?.userName || '',
            },
            closedByUser: {
              firstName: closedByUser?.firstName || '',
              lastName: closedByUser?.lastName || '',
              role: closedByUser?.role || '',
            },
            description: description || '',
            categoryName: categoryName || '—',
            status: status ? status.toUpperCase() : 'OPEN',
            priority: priority || null,
            resolvedAt: ticket?.resolvedAt || null,
            assignUser: assignUser || null,
            department: department
              ? { id: department.id, departmentName: department.departmentName }
              : null,
            duration: duration || '-',
            createdAt: createdAt,
            closedDate: closedDate,
            fileUrl: fileUrl || [],
            currentLevel: currentLevel || null,
            contactMode: contactMode || null,
            ticketLogs: ticketLogs || [],
            isRead:
              typeof isRead === 'string'
                ? isRead
                : JSON.stringify({
                  isAdminRead: true,
                  isClientRead: true,
                  isAssigneeRead: true,
                }),
          }
        }) || []
      setNewTableData(allFormattedData)
    }
  }, [data])

  // Listen for requests to refetch expanded ticket details (after bulk operations)
  useEffect(() => {
    const handler = (e) => {
      const ids = (e && e.detail) || []
      if (Array.isArray(ids) && ids.length) {
        ids.forEach((id) => {
          const idNum = Number(id)
          if (!Number.isNaN(idNum)) {
            fetchTicketDetails(idNum, true)
          }
        })
      }
    }
    window.addEventListener('refetchExpandedTickets', handler)
    return () => window.removeEventListener('refetchExpandedTickets', handler)
    // eslint-disable-next-line
  }, [expandedRowKeys])

  useEffect(() => {
    let filteredData = newTableData

    if (filters.status && filters.status !== 'All') {
      filteredData = newTableData.filter(
        (item) => item.status === filters.status
      )
    }

    setNewTableData(filteredData)
    //eslint-disable-next-line
  }, [filters.status])

  const logsColumn = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      width: 120,
      render: (text) => (text ? text : '-'),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 500,
      render: (text, record) => {
        if (!text && !record?.createdAt) return '-'

        // const formattedDate =
        //   record?.createdAt && moment(record.createdAt).isValid()
        //     ? `[${moment(record.createdAt).format('DD-MM-YYYY hh:mm A')}]`
        //     : ''

        const formattedDescription = text
          ? text.replace('(SALES_EXECUTIVE)', '(AIF_SALES_EXECUTIVE)')
          : ''

        return <span>{formattedDescription}</span>
      },
    },
    {
      title: 'Status By',
      dataIndex: 'statusChanger',
      key: 'statusChanger',
      align: 'left',
      width: 200,
      render: (record) => (record ? getName(record) : '-'),
    },
    {
      title: 'Status Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center',
      width: 180,
      render: (date) =>
        date && moment(date).isValid()
          ? moment(date).format('DD-MM-YYYY hh:mm A')
          : '-',
      sorter: (a, b) =>
        new Date(a?.createdAt || 0) - new Date(b?.createdAt || 0),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Attachment',
      dataIndex: 'fileUrl',
      key: 'fileUrl',
      align: 'center',
      width: 100,
      render: (text, record) =>
        record?.fileUrl && record.fileUrl.length > 0 ? (
          <LinkOutlined
            className='multiple_download_icon'
            onClick={() => {
              if (canDownload) {
                downloadInZip(record, 'log')
              } else {
                setDocViewer({
                  visible: true,
                  fileUrl: record.fileUrl[0],
                  files: record.fileUrl,
                  title: `Ticket #${record?.ticketNumber || ''} Attachments`,
                })
              }
            }}
            style={{ cursor: 'pointer', color: '#1890ff' }}
          />
        ) : (
          '-'
        ),

    },
  ]

  useEffect(() => {
    client
      .query({
        query: GET_USERS,
        variables: {
          where: {
            role: ['STAFF', 'SALES_EXECUTIVE', 'ADMIN'],
            isActive: 'true',
          },
          limit: 100000,
        },
      })
      .then((res) => {
        client
          .query({
            query: GET_TICKET_USERS,
          })
          .then((ticketRes) => {
            setUserData(
              uniqBy(
                [
                  ...ticketRes?.data?.getTicketUsers,
                  ...res?.data?.getUsers?.users,
                ],
                'id'
              )
            )
          })
          .catch((ticketErr) => {
            setUserError(ticketErr)
          })
      })
      .catch((err) => {
        setUserError(err)
      })
  }, [isClient, isAssociate, isMasterAssociate])

  function handlePagination(page) {
    setSkip((page - 1) * 10)
    setCurrentPage(page)
  }

  function handlePageSizeChange(current, size) {
    setLimit(size)
  }

  const handleSort = (columnKey) => {
    setSortInfo((prev) => {
      const newOrder =
        prev.columnKey === columnKey
          ? prev.order === 'ascend'
            ? 'descend'
            : prev.order === 'descend'
              ? null
              : 'ascend'
          : 'ascend'

      return {
        columnKey: newOrder ? columnKey : null,
        order: newOrder,
      }
    })
  }

  function handleSearch(value, confirm, filedName) {
    value = typeof value === 'object' ? value[0] : value
    confirm()
    const tempFilters = JSON.parse(JSON.stringify(filters))
    tempFilters[filedName] = value
    setFilters(tempFilters)
    setCurrentPage(1)
  }

  function handleReset(clearFilters, filedName) {
    clearFilters()
    const tempFilters = JSON.parse(JSON.stringify(filters))
    if (tempFilters[filedName]) {
      delete tempFilters[filedName]
    }
    setFilters(tempFilters)
    setCurrentPage(1)
  }

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type='primary'
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size='small'
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size='small'
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase())
        : '',
  })

  const handlePriorityChange = (newPriority, record) => {
    const priorityConfig = slaConfigData.find((cfg) => String(cfg.id) === String(newPriority))
    const priorityLabel = priorityConfig?.priorityLevel || String(newPriority)

    // determine current priority id (handles both object and primitive cases)
    const currentPriorityId = record?.priority?.id ?? record?.priority
    if (String(currentPriorityId) === String(newPriority)) {
      // same priority selected — do nothing (no logs, no server mutate)
      openNotification('info', 'Selected priority is the same as current priority')
      return
    }

    setNewTableData((prev) =>
      prev.map((t) =>
        t.id === record.id
          ? { ...t, priority: priorityConfig || { id: +newPriority, priorityLevel: priorityLabel } }
          : t,
      ),
    )

    // prepare optimistic log (available regardless of expanded state)
    const nowIso = new Date().toISOString()
    // include old and new priority labels in optimistic description
    const currentPriorityIdLocal = record?.priority?.id ?? record?.priority
    const oldPriorityConfig = slaConfigData.find(
      (cfg) => String(cfg.id) === String(currentPriorityIdLocal)
    )
    const oldPriorityLabel =
      oldPriorityConfig?.priorityLevel ||
      record?.priority?.priorityLevel ||
      String(currentPriorityIdLocal)

    const optimisticLog = {
      id: `optimistic-priority-${nowIso}`,
      status: record.status,
      userId: currentUser.id,
      ticketId: record.id,
      description: `Ticket priority changed ${oldPriorityLabel} to ${priorityLabel}. by ${currentUser.firstName} ${currentUser.lastName}`,
      isStatusChange: false,
      createdAt: nowIso,
      statusChanger: {
        id: currentUser.id,
        firstName: currentUser.firstName || '',
        lastName: currentUser.lastName || '',
      },
    }

    // insert into expanded details if present
    if (expandedRowKeys.includes(record.id)) {
      setExpandedTicketDetails((prev) => {
        const existing = prev[record.id] || { ticketLogs: [] }
        return {
          ...prev,
          [record.id]: {
            ...existing,
            ticketLogs: [optimisticLog, ...(existing.ticketLogs || [])],
          },
        }
      })
    }

    // always prepend optimistic log to the table row's ticketLogs so expanded view (if not yet fetched) shows it
    setNewTableData((prev) =>
      prev.map((t) =>
        t.id === record.id
          ? { ...t, ticketLogs: [{ ...optimisticLog }, ...(t.ticketLogs || [])] }
          : t,
      ),
    )

    client
      .mutate({
        mutation: NEW_UPDATE_TICKET,
        variables: {
          id: record.id,
          data: { priorityId: +newPriority },
        },
        refetchQueries: [
          {
            query: GET_NEW_TICKETS,
            variables: { where: filters, limit, skip },
            fetchPolicy: 'network-only',
          },
        ],
      })
      .then(async () => {
        openNotification('success', 'Priority updated successfully')
        try {
          await refetchTicketDetailsIfExpanded(record.id)
        } catch (err) {
          console.log(err, 'err')
        }
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('refetchTickets'))
        }
      })
      .catch(() => { })
  }
  const fetchSLAConfigStatus = async () => {
    try {
      const res = await client.query({
        query: GET_PRIORITY_SLA_CONFIG,
        fetchPolicy: 'network-only',
      })

      const data = res?.data?.getPrioritySlaConfigs || []
      setSlaConfigData(data)
    } catch (err) {
      // Error fetching SLA config
    }
  }

  useEffect(() => {
    fetchSLAConfigStatus()
  }, [])

  const assignTicket = (id, data) => {
    const ticketId = data?.id
    setAssigningAgentIds((prev) => [...prev, ticketId])
    client
      .mutate({
        mutation: NEW_UPDATE_TICKET,
        variables: { data: { assignUserId: parseInt(id) }, id: ticketId },
        refetchQueries: [
          {
            query: GET_NEW_TICKETS,
            variables: { where: filters, limit, skip },
            fetchPolicy: 'network-only',
          },
        ],
      })
      .then((res) => {
        openNotification('success', 'Ticket Assigned Successfully')

        const updatedUser = userData.find((u) => String(u.id) === String(id))
        const updatedAssignUser = updatedUser ? {
          firstName: updatedUser?.firstName || '',
          lastName: updatedUser?.lastName || '',
          id: updatedUser?.id,
        } : null

        setNewTableData((prev) =>
          prev.map((ticket) =>
            ticket.id === ticketId
              ? {
                ...ticket,
                assignUser: updatedAssignUser,
              }
              : ticket
          )
        )
        const ticketIdNum = Number(ticketId)
        if (expandedRowKeys.includes(ticketIdNum)) {
          fetchTicketDetails(ticketIdNum, true)
        }
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('refetchTickets'))
        }
      })
      .catch(() => { })
      .finally(() => {
        setAssigningAgentIds((prev) => prev.filter((tId) => tId !== ticketId))
      })
  }

  const assignDepartment = (id, data) => {
    const ticketId = data?.id
    setAssigningDepartmentIds((prev) => [...prev, ticketId])
    client
      .mutate({
        mutation: NEW_UPDATE_TICKET,
        variables: { data: { departmentId: parseInt(id) }, id: ticketId },
        refetchQueries: [
          {
            query: GET_NEW_TICKETS,
            variables: { where: filters, limit, skip },
            fetchPolicy: 'network-only',
          },
        ],
      })
      .then(() => {
        openNotification('success', 'Department Updated Successfully')

        const updatedDept = departmentData.find((d) => String(d.id) === String(id))
        const updatedDepartment = updatedDept
          ? {
            id: updatedDept.id,
            departmentName: updatedDept.departmentName || '',
          }
          : null

        setNewTableData((prev) =>
          prev.map((ticket) =>
            ticket.id === ticketId
              ? {
                ...ticket,
                department: updatedDepartment,
              }
              : ticket
          )
        )
        const ticketIdNum = Number(ticketId)
        if (expandedRowKeys.includes(ticketIdNum)) {
          fetchTicketDetails(ticketIdNum, true)
        }
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('refetchTickets'))
        }
      })
      .catch(() => { })
      .finally(() => {
        setAssigningDepartmentIds((prev) =>
          prev.filter((tId) => tId !== ticketId)
        )
      })
  }

  const handleStatusChange = (newStatus, record) => {
    const updatedStatus = newStatus === 'TRIAGED' ? 'TRIAGED' : newStatus

    if (updatedStatus === 'CLOSED') {
      setSelectedTicket(record)
      setIsReOpen(false)
      setShowCloseButton(true)
      setOpenReplyModal(true)
      return
    }
    setNewTableData((prev) =>
      prev.map((t) => (t.id === record.id ? { ...t, status: updatedStatus } : t)),
    )

    if (expandedRowKeys.includes(record.id)) {
      const nowIso = new Date().toISOString()
      const statusChanger = {
        id: currentUser.id,
        firstName: currentUser.firstName || '',
        lastName: currentUser.lastName || '',
      }
      const optimisticLog = {
        id: `optimistic-${nowIso}`,
        status: updatedStatus,
        userId: currentUser.id,
        ticketId: record.id,
        description: `Ticket status changed to ${updatedStatus} by ${currentUser.firstName} ${currentUser.lastName} (${currentUser.role})`,
        isStatusChange: true,
        createdAt: nowIso,
        statusChanger,
        fileUrl: [],
      }
      setExpandedTicketDetails((prev) => {
        const existing = prev[record.id] || { ticketLogs: [] }
        return {
          ...prev,
          [record.id]: {
            ...existing,
            ticketLogs: [optimisticLog, ...(existing.ticketLogs || [])],
          },
        }
      })
    }

    client
      .mutate({
        mutation: NEW_UPDATE_TICKET,
        variables: {
          id: record.id,
          data: { status: updatedStatus },
        },
        refetchQueries: [
          {
            query: GET_NEW_TICKETS,
            variables: { where: filters, limit, skip },
            fetchPolicy: 'network-only',
          },
        ],
      })
      .then(async () => {
        openNotification('success', 'Status updated successfully')
        try {
          await refetchTicketDetailsIfExpanded(record.id)
        } catch (err) {
          console.log(err, 'err')
        }
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('refetchTickets'))
        }
      })
      .catch(() => { })
  }

  function handleCloseTicket(values, resetForm) {
    setCloseLoading(true)

    let files = []
    if (values?.fileUrl) {
      values.fileUrl.forEach((file) => {
        files.push(file?.originFileObj)
      })
    } else {
      delete values.fileUrl
    }

    const data = {
      reason: values.description || values.reason,
      status: 'CLOSED',
      closeById: currentUser.id,
      fileUrl: files,
    }
    client
      .mutate({
        mutation: NEW_UPDATE_TICKET,
        variables: { data, id: selectedTicket?.id },
        refetchQueries: [
          {
            query: GET_TICKET,
            variables: { id: selectedTicket?.id },
            fetchPolicy: 'network-only',
          },
          {
            query: GET_NEW_TICKETS,
            variables: { where: filters, limit, skip },
            fetchPolicy: 'network-only',
          },
        ],
      })
      .then(async (res) => {
        // Refetch the ticket details to update the expanded row
        await refetchTicketDetailsIfExpanded(selectedTicket?.id)
        openNotification('success', 'Ticket Closed Successfully')
        setCloseLoading(false)
        setShowFormModal(false)
        setSelectedTicket({})
        setOpenReplyModal(false)
        resetForm()
      })
      .catch(() => {
        setCloseLoading(false)
      })
  }

  const fetchTicketDetails = async (ticketId, forceRefetch = false) => {
    if (!forceRefetch && expandedTicketDetails[ticketId]) {
      // Already fetched, don't fetch again unless forced
      return
    }

    setLoadingTicketDetails((prev) => ({ ...prev, [ticketId]: true }))
    try {
      const res = await client.query({
        query: GET_NEW_TICKET,
        variables: { id: ticketId },
        fetchPolicy: 'network-only',
      })
      if (res?.data?.newGetTicket) {
        setExpandedTicketDetails((prev) => ({
          ...prev,
          [ticketId]: res.data.newGetTicket,
        }))
      }
    } catch (err) {
      // Error fetching ticket details
    } finally {
      setLoadingTicketDetails((prev) => ({ ...prev, [ticketId]: false }))
    }
  }

  const refetchTicketDetailsIfExpanded = async (ticketId) => {
    if (ticketId && expandedRowKeys.includes(ticketId)) {
      try {
        const res = await client.query({
          query: GET_NEW_TICKET,
          variables: { id: ticketId },
          fetchPolicy: 'network-only',
        })
        if (res?.data?.newGetTicket) {
          setExpandedTicketDetails((prev) => ({
            ...prev,
            [ticketId]: res.data.newGetTicket,
          }))
        }
      } catch (err) {
        console.log(err, 'err')
      }
    }
  }

  useEffect(() => {
    const assignHandler = (e) => {
      const { ticketIds, assignUser } = (e && e.detail) || {}
      if (!Array.isArray(ticketIds) || ticketIds.length === 0) return
      setNewTableData((prev) =>
        prev.map((t) =>
          ticketIds.includes(t.id)
            ? { ...t, assignUser: assignUser ? { ...assignUser } : t.assignUser }
            : t,
        ),
      )
      ticketIds.forEach((id) => {
        const idNum = Number(id)
        if (!Number.isNaN(idNum)) {
          setExpandedTicketDetails((prev) => {
            if (!prev[idNum]) return prev
            return { ...prev, [idNum]: { ...prev[idNum], assignUser: assignUser ? { ...assignUser } : prev[idNum].assignUser } }
          })
          fetchTicketDetails(idNum, true)
        }
      })
    }

    const statusHandler = (e) => {
      const { ticketIds, status } = (e && e.detail) || {}
      if (!Array.isArray(ticketIds) || ticketIds.length === 0) return
      setNewTableData((prev) =>
        prev.map((t) =>
          ticketIds.includes(t.id) ? { ...t, status: status } : t,
        ),
      )
      ticketIds.forEach((id) => {
        const idNum = Number(id)
        if (!Number.isNaN(idNum)) {
          fetchTicketDetails(idNum, true)
        }
      })
    }

    window.addEventListener('bulkAssignApplied', assignHandler)
    window.addEventListener('bulkStatusApplied', statusHandler)
    return () => {
      window.removeEventListener('bulkAssignApplied', assignHandler)
      window.removeEventListener('bulkStatusApplied', statusHandler)
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [expandedRowKeys])

  // const handleExpand = (record) => {
  //   const recordId = record.id
  //   const isExpanded = expandedRowKeys.includes(recordId)
  //   if (isExpanded) {
  //     setExpandedRowKeys(expandedRowKeys.filter((id) => id !== recordId))
  //   } else {
  //     updateTicketRead(record)
  //     setExpandedRowKeys([...expandedRowKeys, recordId])
  //     fetchTicketDetails(recordId)
  //   }
  // }

  const getLastUpdateMoment = (record) => {
    if (!record?.ticketLogs?.length) {
      return null
    }

    return record.ticketLogs.reduce((latest, log) => {
      const createdAt = log?.createdAt
      if (!createdAt) return latest

      const logMoment = moment(createdAt)
      if (!logMoment.isValid()) return latest

      if (!latest || logMoment.isAfter(latest)) {
        return logMoment
      }

      return latest
    }, null)
  }

  const columns = [
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      width: 120,
      render: (text, record) => {
        const ticketDetails = expandedTicketDetails[record.id]
        const ticketData = ticketDetails || record

        const fileUrls = []
        if (ticketData?.fileUrl?.length) fileUrls.push(...ticketData.fileUrl)
        if (ticketData?.ticketLogs) {
          ticketData.ticketLogs.forEach((item) => {
            if (item?.fileUrl?.length) fileUrls.push(...item?.fileUrl)
          })
        }

        const hasAttachments = fileUrls.length > 0
        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
            <Tooltip title='Download Attachments'>
              <img
                src={hasAttachments ? Attachment : NoAttachment}
                className='attachment-icon'
                alt={hasAttachments ? 'attachment' : 'no-attachment'}
                onClick={(e) => {
                  e.stopPropagation()
                  downloadInZip(ticketData, 'main')
                }}
                style={{ cursor: 'pointer', display: 'inline-block', opacity: hasAttachments ? 1 : 0.5 }}
              />
            </Tooltip>
            <Tooltip title='Send Reply'>
              <SendOutlined
                className='expand_icon'
                onClick={(e) => {
                  e.stopPropagation()
                  setSelectedTicket(record)
                  setOpenReplyModal(true)
                }}
                style={{ cursor: 'pointer', fontSize: '14px' }}
              />
            </Tooltip>
          </div>
        )
      },
    },
    {
      title: 'Ticket Number',
      dataIndex: 'ticketNumber',
      key: 'ticketNumber',
      align: 'left',
      width: 180,
      sorter: (a, b) => a.ticketNumber - b.ticketNumber,
      sortOrder: sortInfo.columnKey === 'ticketNumber' ? sortInfo.order : null,
      render: (text) => `#${text}`,
      ...getColumnSearchProps('ticketNumber'),
    },
    {
      title: 'Client Name',
      dataIndex: 'clientName',
      key: 'clientName',
      align: 'left',
      width: 180,
      sorter: (a, b) => {
        const nameA = `${a?.generateFor?.firstName || ''} ${a?.generateFor?.lastName || ''
          }`.toLowerCase()
        const nameB = `${b?.generateFor?.firstName || ''} ${b?.generateFor?.lastName || ''
          }`.toLowerCase()
        return nameA.localeCompare(nameB)
      },
      sortOrder: sortInfo.columnKey === 'clientName' ? sortInfo.order : null,
      render: (text, record) => {
        const user = record?.generateFor
        if (!user) return '-'
        return `${user.firstName || ''} ${user.lastName || ''}`.trim()
      },
      ...getColumnSearchProps('clientName'),
    },
    {
      title: 'Client Code',
      dataIndex: 'clientCode',
      key: 'clientCode',
      align: 'left',
      width: 180,
      render: (text, record) => {
        const user = record?.generateFor
        if (!user) return '-'
        return `${user.clientCode || ''}`.trim()
      },
      ...getColumnSearchProps('clientCode'),
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>Contact Mode</span>
        </div>
      ),
      dataIndex: 'contactMode',
      key: 'contactMode',
      align: 'center',
      width: 200,
      render: (text, record) => {
        if (!text) return '-'
        return text
      },
    },

    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>Issue Summary</span>
        </div>
      ),
      dataIndex: 'description',
      key: 'description',
      align: 'left',
      width: 200,
      render: (text, record) => {
        if (!text) return "Can't Access Report";

        const limit = 50;
        const rowKey = record.id || record.key;
        const isLong = text.length > limit;
        const expanded = isSeeMore[record.key];

        return (
          <span>
            {expanded || !isLong ? text : `${text.slice(0, limit)}`}

            {isLong && (
              <span
                style={{
                  marginLeft: 6,
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
                onClick={() =>
                  setIsSeeMore(prev => ({
                    ...prev,
                    [rowKey]: !expanded,
                  }))
                }
              >
                {expanded ? 'Show less...' : 'Show more...'}
              </span>
            )}
          </span>
        );
      },
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      align: 'left',
      width: 120,
      sorter: (a, b) => {
        const nameA = (a.department?.departmentName || '').toLowerCase()
        const nameB = (b.department?.departmentName || '').toLowerCase()
        return nameA.localeCompare(nameB)
      },
      sortOrder: sortInfo.columnKey === 'department' ? sortInfo.order : null,
      render: (text, record) => {
        const isUnassigned = !record.department?.id
        const currentDeptId = record.department?.id
        const name = isUnassigned
          ? 'Unassigned'
          : record.department?.departmentName || '-'

        const handleAssign = (dept) => {
          assignDepartment(dept.id, record)
        }

        const menu = (
          <Menu
            onClick={({ key }) => {
              const selectedDept = (departmentData || []).find(
                (d) => String(d.id) === key
              )
              if (selectedDept) handleAssign(selectedDept)
            }}
          >
            <Menu.Item
              key='__search_department'
              disabled
              style={{ cursor: 'default', background: 'transparent' }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                style={{ pointerEvents: 'auto', width: '100%' }}
              >
                <Input
                  autoFocus
                  allowClear
                  placeholder='Search department'
                  value={departmentSearch}
                  onChange={(e) => setDepartmentSearch(e.target.value)}
                  style={{ borderRadius: 8 }}
                />
              </div>
            </Menu.Item>
            <Menu.Divider />
            {(filteredDepartments || [])
              .filter((dept) => String(dept.id) !== String(currentDeptId))
              .map((dept) => (
                <Menu.Item key={String(dept.id)} style={{ width: '100%' }}>
                  <span>{dept.departmentName}</span>
                </Menu.Item>
              ))}
          </Menu>
        )
        const isAssigning = assigningDepartmentIds.includes(record.id)

        if (isAssigning && !isClient) {
          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Dropdown
                overlay={menu}
                trigger={['click']}
                onVisibleChange={setDropdownVisible}
              >
                <span>Updating...</span>
              </Dropdown>
            </div>
          )
        }

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span style={{ fontSize: '13px', fontWeight: '400' }}>{name}</span>
            {!isClient && (
              <Dropdown
                overlay={menu}
                trigger={['click']}
                onVisibleChange={setDropdownVisible}
              >
                <DownOutlined
                  style={{
                    fontSize: 10,
                    color: '#999',
                    cursor: 'pointer',
                    marginLeft: '8px',
                  }}
                />
              </Dropdown>
            )}
          </div>
        )
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'left',
      width: 120,
      sorter: (a, b) => {
        const getStatusOrder = (status) => {
          switch (status) {
            case 'NEW':
              return 1
            case 'OPEN':
              return 2
            case 'ASSIGNED':
              return 3
            case 'ESCALATED':
              return 4
            case 'BREACHED':
              return 5
            case 'RESOLVED':
              return 6
            case 'CLOSED':
              return 7
            default:
              return 8
          }
        }
        return getStatusOrder(a.status) - getStatusOrder(b.status)
      },
      sortOrder: sortInfo.columnKey === 'status' ? sortInfo.order : null,
      render: (status, record) => {
        const key = (status || '').toUpperCase()
        const tagStyle = statusPalette[key] || { bg: '#D9D9D9', color: '#000' }

        if (
          isClient ||
          isMasterAssociate ||
          isSalesExecutive ||
          isAssociate
        ) {
          // 👨‍💼 Client view → stays same
          return (
            <Tag
              className='ticket-status-tag'
              style={{
                borderRadius: '4px',
                border: 'none',
                fontSize: '14px',
                fontWeight: '700',
                color: tagStyle.color,
                backgroundColor: tagStyle.bg,
                display: 'flex',
                alignItems: 'center',
                padding: '6px 12px',
                cursor: 'pointer',
                justifyContent: 'center',
              }}
            >
              {status.charAt(0) + status.slice(1).toLowerCase()}
            </Tag>
          )
        } else {
          // ✅ Decide options based on role
          let statusOptions = []

          if (isStaff) {
            statusOptions = ['OPEN', 'RESOLVED']
          } else if (currentUser.id === record.assignUser?.id) {
            statusOptions = ['INPROGRESS', 'RESOLVED'] // Assignee
          } else {
            statusOptions = [
              'OPEN',
              'BREACHED',
              'ESCALATED',
              'RESOLVED',
              'CLOSED',
            ] // Admin/others
          }

          const menu = (
            <Menu onClick={({ key }) => handleStatusChange(key, record)}>
              {statusOptions.map((statusOption) => (
                <Menu.Item key={statusOption}>
                  {statusOption.charAt(0) + statusOption.slice(1).toLowerCase()}
                </Menu.Item>
              ))}
            </Menu>
          )

          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {key !== 'NEW' ? (
                <Dropdown overlay={menu} trigger={['click']}>
                  <Tag
                    className='ticket-status-tag'
                    style={{
                      borderRadius: '4px',
                      border: 'none',
                      fontSize: '14px',
                      fontWeight: '700',
                      color: tagStyle.color,
                      backgroundColor: tagStyle.bg,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '6px 12px',
                      cursor: 'pointer',
                    }}
                  >
                    {status.charAt(0) + status.slice(1).toLowerCase()}
                    <DownOutlined
                      style={{ fontSize: 10, color: tagStyle.color }}
                    />
                  </Tag>
                </Dropdown>
              ) : (
                <Tag
                  className='ticket-status-tag'
                  style={{
                    borderRadius: '4px',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '700',
                    color: tagStyle.color,
                    backgroundColor: tagStyle.bg,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '6px 12px',
                  }}
                >
                  {status.charAt(0) + status.slice(1).toLowerCase()}
                </Tag>
              )}
            </div>
          )
        }
      },
    },

    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 120,
      align: 'left',
      sorter: (a, b) => {
        const normalizePriorityField = (value) =>
          value?.id ?? value ?? null

        const getPriorityOrder = (priorityField) => {
          const priorityLevel = slaConfigData.find(
            (cfg) =>
              String(cfg.id) ===
              String(normalizePriorityField(priorityField))
          )?.priorityLevel

          switch (priorityLevel) {
            case 'High':
              return 3
            case 'Medium':
              return 2
            case 'Low':
              return 1
            default:
              return 0
          }
        }

        return getPriorityOrder(a.priority) - getPriorityOrder(b.priority)
      },
      sortOrder: sortInfo.columnKey === 'priority' ? sortInfo.order : null,
      render: (text, record) => {
        const currentPriorityId = record?.priority?.id ?? record?.priority ?? null
        const config = slaConfigData.find(
          (cfg) => String(cfg.id) === String(currentPriorityId)
        )

        const priorityValue = config?.priorityLevel || 'Priority not decided'
        const icon =
          priorityValue === 'high' || priorityValue === 'medium'
            ? '⚠️'
            : priorityValue === 'low'
              ? '✓'
              : ''

        if (isClient) {
          return (
            <Tag
              color={priorityColor[priorityValue?.toLowerCase()]}
              style={{
                borderRadius: '4px',
                border: 'none',
                fontSize: '12px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px 8px',
                cursor: 'pointer',
              }}
            >
              {icon} {priorityValue}
            </Tag>
          )
        } else {
          const menu = (
            <Menu
              onClick={({ key }) => {
                const selectedConfig = slaConfigData.find(
                  (cfg) => cfg.priorityLevel === key
                )
                if (selectedConfig) {
                  handlePriorityChange(selectedConfig.id, record)
                }
              }}
            >
              {slaConfigData.map((item) => (
                <Menu.Item key={item.priorityLevel}>
                  {item.priorityLevel}
                </Menu.Item>
              ))}
            </Menu>
          )

          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Dropdown overlay={menu} trigger={['click']}>
                <Tag
                  color={priorityColor[priorityValue?.toLowerCase()]}
                  style={{
                    borderRadius: '4px',
                    border: 'none',
                    fontSize: '12px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    cursor: 'pointer',
                  }}
                >
                  {icon} {priorityValue}
                  <DownOutlined style={{ fontSize: 10, color: '#999' }} />
                </Tag>
              </Dropdown>
            </div>
          )
        }
      },
    },
    {
      title: 'SLA Time',
      dataIndex: 'slaTime',
      key: 'slaTime',
      align: 'left',
      width: 130,
      sorter: (a, b) => {
        const { rawValue: aRawValue = 0 } = calculateRealTimeSLATime(a)
        const { rawValue: bRawValue = 0 } = calculateRealTimeSLATime(b)
        return bRawValue - aRawValue
      },
      sortOrder: sortInfo.columnKey === 'slaTime' ? sortInfo.order : null,
      render: (text, record) => {
        const { label, isNegative } = calculateRealTimeSLATime(record)
        const slaColor = getSLAColor(record)

        if (label === '—') {
          return <span style={{ color: '#999' }}>{label}</span>
        }

        const timerBadge = (
          <span
            style={{
              background: slaColor.background,
              color: slaColor.color,
              padding: '2px 6px',
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 700,
              animation: isNegative ? 'blink 1s infinite' : 'none',
            }}
          >
            {label}
          </span>
        )

        if (record?.status === 'RESOLVED') {
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <span style={{  color: '#555' }}>Resolved After</span>
              {timerBadge}
            </div>
          )
        }

        return timerBadge
      },
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span>Agent</span>
        </div>
      ),
      dataIndex: 'agent',
      key: 'agent',
      align: 'left',
      width: 160,
      render: (text, record) => {
        const isUnassigned = !record.assignUser
        const currentUserId = record.assignUser?.id

        const name = isUnassigned
          ? 'Unassigned'
          : `${record.assignUser.firstName || ''} ${record.assignUser.lastName || ''
            }`.trim()

        const handleAssign = (user) => {
          assignTicket(user.id, record)
        }
        const menu = (
          <Menu
            onClick={({ key }) => {
              const selectedUser = (userData || []).find(
                (u) => String(u.id) === key
              )
              if (selectedUser) handleAssign(selectedUser)
            }}
          >
            <Menu.Item
              key='__search_agent'
              disabled
              style={{ cursor: 'default', background: 'transparent' }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                style={{ pointerEvents: 'auto', width: '100%' }}
              >
                <Input
                  autoFocus
                  allowClear
                  placeholder='Search agent'
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  style={{ borderRadius: 8 }}
                />
              </div>
            </Menu.Item>
            <Menu.Divider />
            {(filteredUsers || [])
              .filter((user) => user.id !== currentUserId)
              .map((user) => (
                <Menu.Item key={String(user.id)} style={{ width: '100%' }}>
                  <span
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <span>
                      {user.firstName} {user.lastName}
                    </span>
                    <span
                      style={{
                        marginLeft: 8,
                        color: '#999',
                        fontStyle: 'italic',
                      }}
                    >
                      ({user.role || 'INTERNAL TEAM'})
                    </span>
                  </span>
                </Menu.Item>
              ))}
          </Menu>
        )
        const isAssigning = assigningAgentIds.includes(record.id)

        if (isAssigning && !isClient) {
          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Dropdown
                overlay={menu}
                trigger={['click']}
                onVisibleChange={setDropdownVisible}
              >
                <span>Updating...</span>
              </Dropdown>
            </div>
          )
        }

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span style={{ fontSize: '13px', fontWeight: '400' }}>{name}</span>
            {!isClient && (
              <Dropdown
                overlay={menu}
                trigger={['click']}
                onVisibleChange={setDropdownVisible}
              >
                <DownOutlined
                  style={{
                    fontSize: 10,
                    color: '#999',
                    cursor: 'pointer',
                    marginLeft: '8px',
                  }}
                />
              </Dropdown>
            )}
          </div>
        )
      },
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>Created By</span>
        </div>
      ),
      dataIndex: 'createdBy',
      key: 'createdBy',
      align: 'left',
      width: 200,
      render: (text, record) => {
        const user = record?.generatedBy

        if (!user || (!user.firstName && !user.lastName && !user.role)) {
          return '-' // show dash when user exists but has empty values
        }

        return record?.generatedBy
          ? `${user.firstName || ''} ${user.lastName || ''} (${user.role || ''
            })`.trim()
          : '-'
      },
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>Closed By</span>
        </div>
      ),
      dataIndex: 'closedBy',
      key: 'closedBy',
      align: 'left',
      width: 200,
      render: (text, record) => {
        const user = record?.closedByUser
        if (!user) return '-'
        return record?.closedByUser?.firstName !== ''
          ? `${user.firstName || ''} ${user.lastName || ''} (${user.role || ''
            })`.trim()
          : '-'
      },
    },
    {
      title: (
        <div
          onClick={() => handleSort('createdAt')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
        >
          <span>Created At</span>
        </div>
      ),
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'left',
      width: 200,
      sorter: (a, b) => {
        return a.createdAt - b.createdAt
      },
      sortOrder: sortInfo.columnKey === 'createdAt' ? sortInfo.order : null,
      render: (text, record) => {
        return record?.createdAt && moment(record.createdAt).isValid()
          ? moment(record.createdAt).format('DD-MM-YYYY hh:mm A')
          : '—'
      },
    },
    {
      title: (
        <div
          onClick={() => handleSort('closedDate')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
        >
          <span>Closed At</span>
        </div>
      ),
      dataIndex: 'closedDate',
      key: 'closedDate',
      align: 'left',
      width: 200,
      sorter: (a, b) => {
        return a.closedDate - b.closedDate
      },
      sortOrder: sortInfo.columnKey === 'closedDate' ? sortInfo.order : null,
      render: (text, record) => {
        return record?.closedDate && moment(record.closedDate).isValid()
          ? moment(record.closedDate).format('DD-MM-YYYY hh:mm A')
          : '—'
      },
    },
    {
      title: (
        <div
          onClick={() => handleSort('lastStatusTimestamp')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
        >
          <span>Last Status Timestamp</span>
        </div>
      ),
      dataIndex: 'ticketLogs',
      key: 'lastStatusTimestamp',
      align: 'left',
      width: 200,
      sorter: (a, b) => {
        const aMoment = getLastUpdateMoment(a)
        const bMoment = getLastUpdateMoment(b)
        const aValue = aMoment ? aMoment.valueOf() : 0
        const bValue = bMoment ? bMoment.valueOf() : 0
        return aValue - bValue
      },
      sortOrder:
        sortInfo.columnKey === 'lastStatusTimestamp' ? sortInfo.order : null,
      render: (text, record) => {
        const lastMoment = getLastUpdateMoment(record)
        return lastMoment && lastMoment.isValid()
          ? lastMoment.format('DD-MM-YYYY hh:mm A')
          : '—'
      },
    },
  ]

  const handleReplyTicket = (values, resetForm) => {
    setReplyLoading(true)
    let files = []
    if (values?.fileUrl) {
      values.fileUrl.forEach((file) => {
        files.push(file?.originFileObj)
      })
      values.fileUrl = files
    } else {
      delete values.fileUrl
    }
    client
      .mutate({
        mutation: REPLY_TICKET_MODULE,
        variables: {
          data: {
            replyDescription: values?.description,
            fileUrl: values?.fileUrl,
          },
          where: { id: selectedTicket?.id },
        },
        refetchQueries: [
          {
            query: GET_TICKET,
            variables: { id: selectedTicket?.id },
            fetchPolicy: 'network-only',
          },
          {
            query: GET_NEW_TICKETS,
            variables: { where: filters, limit, skip },
            fetchPolicy: 'network-only',
          },
        ],
      })
      .then(async (res) => {
        // Refetch the ticket details to update the expanded row
        await refetchTicketDetailsIfExpanded(selectedTicket?.id)
        setReplyLoading(false)
        setSelectedTicket({})
        setOpenReplyModal(false)
        resetForm()
        openNotification('success', 'Reply sent successfully')
      })
      .catch(() => {
        setReplyLoading(false)
        setSelectedTicket({})
        setOpenReplyModal(false)
        resetForm()
      })
      .finally(() => {
        setReplyLoading(false)
        setSelectedTicket({})
        setOpenReplyModal(false)
        resetForm()
      })
  }

  const reOpenTicket = (values, resetForm) => {
    setReplyLoading(true)
    let files = []
    if (values?.fileUrl) {
      values.fileUrl.forEach((file) => {
        files.push(file?.originFileObj)
      })
      values.fileUrl = files
    } else {
      delete values.fileUrl
    }

    client
      .mutate({
        mutation: NEW_UPDATE_TICKET,
        variables: {
          data: {
            description: values?.description,
            fileUrl: values?.fileUrl,
            status: 'REOPEN',
          },
          id: selectedTicket?.id,
        },
        refetchQueries: [
          {
            query: GET_TICKET,
            variables: { id: selectedTicket?.id },
            fetchPolicy: 'network-only',
          },
          {
            query: GET_NEW_TICKETS,
            variables: { where: filters, limit, skip },
            fetchPolicy: 'network-only',
          },
        ],
      })
      .then(async (res) => {
        // Refetch the ticket details to update the expanded row
        await refetchTicketDetailsIfExpanded(selectedTicket?.id)
        setReplyLoading(false)
        setSelectedTicket({})
        setIsReOpen(false)
        setOpenReplyModal(false)
        resetForm()
        openNotification('success', 'Ticket Re-Open successfully')
      })
      .catch(() => {
        setReplyLoading(false)
        setIsReOpen(false)
        setSelectedTicket({})
        setOpenReplyModal(false)
        resetForm()
      })
      .finally(() => {
        setReplyLoading(false)
        setIsReOpen(false)
        setSelectedTicket({})
        setOpenReplyModal(false)
        resetForm()
      })
  }

  const downloadInZip = (data, type) => {
    const files = data?.fileUrl || []
    if (type === 'main') {
      if (data?.ticketLogs?.length) {
        data.ticketLogs.forEach((item) => {
          if (item?.fileUrl?.length) {
            files.push(...item?.fileUrl)
          }
        })
      }
    }
    if (files?.length) {
      const pdfUrls = files

      const zip = new JSZip()
      const promises = []

      pdfUrls.forEach((url) => {
        const filename = url.substring(url.lastIndexOf('/') + 1)
        promises.push(
          fetch(url)
            .then((response) => response.blob())
            .then((blob) => zip.file(filename, blob))
        )
      })

      Promise.all(promises)
        .then(() => zip.generateAsync({ type: 'blob' }))
        .then((content) => {
          const zipFilename =
            type === 'main'
              ? `Attachment of ${data?.ticketNumber}`
              : `Attachment of ${data?.ticketNumber} ticket log`
          const url = window.URL.createObjectURL(content)
          const a = document.createElement('a')
          a.href = url
          a.download = zipFilename
          a.click()
          window.URL.revokeObjectURL(url)
        })
        .catch(() => { })
    } else {
      openNotification('warning', 'No attachment found for this ticket.')
    }
  }

  const updateTicketRead = (record) => {
    let read = {}
    try {
      read = JSON.parse(record?.isRead || '{}')
    } catch (e) {
      read = {}
    }

    if (
      !read?.isAdminRead ||
      !read?.isClientRead ||
      !read?.isAssigneeRead ||
      !read?.isAnother
    ) {
      let data = {}
      if (isAdmin) {
        data = { ...read, isAdminRead: true }
      } else if (isClient) {
        data = { ...read, isClientRead: true }
      } else if (
        !isAdmin &&
        !isClient &&
        record?.assignUser?.id === currentUser?.id
      ) {
        data = { ...read, isAssigneeRead: true }
      } else if (
        !isAdmin &&
        !isClient &&
        record?.generatedBy?.id === currentUser?.id
      ) {
        data = { ...read, isAnother: true }
      }

      if (!isEmpty(data)) {
        client
          .mutate({
            mutation: NEW_UPDATE_TICKET,
            variables: {
              data: { isRead: JSON.stringify(data) },
              id: record?.id,
            },
            refetchQueries: [
              {
                query: GET_NEW_TICKETS,
                variables: { where: filters, limit, skip },
                fetchPolicy: 'network-only',
              },
            ],
          })
          .then(() => { })
          .catch(() => { })
      }
    }
  }

  if (error || userError) return <Page404 error={error} />

  return (
    <>
      {!isClient && showFormModal && (
        <CloseTicketModal
          visible={showFormModal}
          onCreate={handleCloseTicket}
          onCancel={() => setShowFormModal(false)}
        />
      )}
      <Table
        rowKey={(record) => (record.key !== undefined ? record.key : record.id)}
        rowSelection={isAdmin ? rowSelection : undefined}
        loading={loading}
        dataSource={newTableData}
        columns={
          (isAdmin
            ? columns
            : showActionButton
              ? columns?.filter((item) => item?.title !== 'User Type')
              : columns?.filter(
                (item) =>
                  item?.title !== 'Assigned To' &&
                  item?.title !== 'User Type' &&
                  item?.key !== 'ticketStatus'
              )
          )?.filter((item) => {
            if (isClient && (item?.title === 'Priority' || item?.title === 'SLA Time')) {
              return false
            }
            return true
          })
        }
        scroll={{ x: true, y: 550 }}
        rowClassName={(record) => {
          let read = {}
          try {
            read = JSON.parse(record?.isRead || '{}')
          } catch (e) { }

          const isUnread =
            (isAdmin && !read.isAdminRead) ||
            (isClient && !read.isClientRead) ||
            (!isAdmin &&
              !isClient &&
              record?.generatedBy?.id === currentUser?.id &&
              !read?.isAnother) ||
            (!isAdmin &&
              !isClient &&
              record?.assignUser?.id === currentUser?.id &&
              !read?.isAssigneeRead)

          return isUnread ? 'bold_unread_row' : ''
        }}
        onChange={(pagination, filters, sorter) => {
          setSortInfo({
            columnKey: sorter.columnKey,
            order: sorter.order,
          })
        }}
        pagination={{
          pageSize: limit,
          total: totalCount,
          onChange: handlePagination,
          current: currentPage,
          onShowSizeChange: handlePageSizeChange,
          showSizeChanger: true,
          showQuickJumper: false,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
          pageSizeOptions: ['10', '20', '50', '100'],
          position: ['bottomRight'],
          style: {
            marginTop: '14px',
            textAlign: 'right',
          },
        }}
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
        expandable={{
          expandedRowKeys: expandedRowKeys,
          onExpandedRowsChange: (expandedKeys) => {
            setExpandedRowKeys(expandedKeys)
          },
          onExpand: (expanded, record) => {
            const recordId = record.id
            if (expanded) {
              updateTicketRead(record)
              setExpandedRowKeys((prev) =>
                prev.includes(recordId) ? prev : [...prev, recordId]
              )
              fetchTicketDetails(recordId)
            } else {
              setExpandedRowKeys((prev) => prev.filter((id) => id !== recordId))
            }
          },
          expandedRowRender: (record) => {
            const ticketDetails = expandedTicketDetails[record.id]
            const rawTicketLogs =
              ticketDetails?.ticketLogs || record?.ticketLogs || []
            const ticketLogs = [...rawTicketLogs].sort(
              (a, b) =>
                new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0)
            )
            const isLoading = loadingTicketDetails[record.id]
            const hasExpandedDetails = Boolean(ticketDetails)
            return (
              <div style={{ margin: '20px', width: '100%', maxWidth: '1200px' }}>
                {isLoading && !hasExpandedDetails ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '40px',
                      color: '#999',
                      fontSize: '14px',
                    }}
                  >
                    Ticket data loading...
                  </div>
                ) : ticketLogs && ticketLogs.length > 0 ? (
                  <Table
                    dataSource={ticketLogs.map((item, index) => ({
                      key: index,
                      ...item,
                      ticketNumber: record?.ticketNumber,
                    }))}
                    columns={logsColumn}
                    pagination={false}
                    className='tickets-table-1'
                    size='small'
                    scroll={{ x: true }}
                  />
                ) : (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '40px',
                      color: '#999',
                      fontSize: '14px',
                    }}
                  >
                    No activity logs available
                  </div>
                )}

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '20px',
                    gap: '10px',
                  }}
                >
                  {record?.status !== 'CLOSED' ? (
                    <Button
                      type='primary'
                      onClick={() => {
                        setOpenReplyModal(true)
                        setSelectedTicket(record)
                        setShowCloseButton(false)
                      }}
                    >
                      Reply
                    </Button>
                  ) : (
                    ''
                  )}

                  {record?.status === 'CLOSED' && !isLoading &&
                    (isClient || isAdmin ||
                      record?.generateFor?.id === currentUser?.id ||
                      record?.generatedBy?.id === currentUser?.id) ? (
                    <Button
                      type='primary'
                      onClick={() => {
                        setOpenReplyModal(true)
                        setIsReOpen(true)
                        setSelectedTicket(record)
                        setShowCloseButton(false)
                      }}
                    >
                      Re Open
                    </Button>
                  ) : (
                    ''
                  )}
                </div>
              </div>
            )
          },
          showExpandColumn: true,
          expandIconColumnIndex: isAdmin ? 1 : 0,
        }}
      />

      {openReplyModal && (
        <ReplyTicketModal
          isReOpen={isReOpen}
          ticket={selectedTicket}
          visible={openReplyModal}
          showCloseButton={showCloseButton}
          onClose={() => {
            setSelectedTicket({})
            setOpenReplyModal(false)
            setShowCloseButton(false)
            setIsReOpen(false)
          }}
          onReplyTicket={handleReplyTicket}
          onCloseTicket={handleCloseTicket}
          onReOpenTicket={reOpenTicket}
          replyLoading={replyLoading}
          closeLoading={closeLoading}
        />
      )}
      <DocumentViewerModal
        visible={docViewer.visible}
        fileUrl={docViewer.fileUrl}
        files={docViewer.files}
        title={docViewer.title}
        onClose={() => setDocViewer({ visible: false, fileUrl: '', files: [], title: '' })}
        canDownload={canDownload}
        watermark={
          !canDownload
            ? `CONFIDENTIAL AUDIT • ${currentUser?.userName || 'AUDIT'} • ${moment().format('DD-MMM-YYYY')}`
            : ''
        }
      />
    </>
  )
}
